/**
 * Core API Client with Encryption/Decryption
 * 
 * All requests to Core (Express) backend use CryptoJS AES encryption
 * matching the pattern used by auth pages (Login, Signup, etc.)
 * 
 * Request:  body is encrypted → sent as { data: "encrypted_string" }
 * Response: data field is encrypted → decrypted before returning
 * 
 * Uses REACT_APP_SECRETKEY (same key used by auth pages and Core's decryptionRequest middleware)
 */

import CryptoJS from 'crypto-js';
import { getAppSecretKey, getAuthToken } from './encryptedStorage';

const SECRET_KEY = getAppSecretKey();
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

const resolveCoreApiBaseUrl = () => {
  const isBrowser = typeof window !== 'undefined' && typeof window.location !== 'undefined';
  const currentOrigin = isBrowser ? window.location.origin : '';

  if (API_BASE_URL) {
    try {
      const parsed = new URL(API_BASE_URL, currentOrigin || 'http://localhost');
      const currentHostname = isBrowser ? window.location.hostname : '';

      // Avoid sending production browsers to a localhost API URL.
      if (currentHostname && !LOCAL_HOSTNAMES.has(currentHostname) && LOCAL_HOSTNAMES.has(parsed.hostname)) {
        return isBrowser ? `${currentOrigin}/api` : parsed.toString().replace(/\/+$/, '');
      }

      return parsed.toString().replace(/\/+$/, '');
    } catch {
      return API_BASE_URL.replace(/\/+$/, '');
    }
  }

  return isBrowser ? `${currentOrigin}/api` : 'http://localhost:5000/api';
};

/**
 * Encrypt data using CryptoJS AES
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @returns {string} Encrypted string
 */
export const encryptPayload = (data) => {
  try {
    const jsonString = typeof data === 'string' ? data : JSON.stringify(data);
    return CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
  } catch (error) {
    console.error('Core API encryption failed:', error);
    throw new Error('Failed to encrypt request data');
  }
};

/**
 * Decrypt data using CryptoJS AES
 * @param {string} encryptedData - Encrypted string from Core response
 * @returns {any} Decrypted and parsed data
 */
export const decryptPayload = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    if (!decrypted) {
      throw new Error('Decryption resulted in empty string');
    }
    try {
      return JSON.parse(decrypted);
    } catch {
      return decrypted;
    }
  } catch (error) {
    console.error('Core API decryption failed:', error);
    throw new Error('Failed to decrypt response data');
  }
};

/**
 * Make an encrypted API request to Core backend
 * 
 * @param {string} endpoint - API endpoint path (e.g., '/resume-parsing/list')
 * @param {Object} options - Request options
 * @param {string} [options.method='GET'] - HTTP method
 * @param {Object} [options.body] - Request body (will be encrypted)
 * @param {Object} [options.headers] - Additional headers
 * @param {boolean} [options.skipEncryption=false] - Skip body encryption (e.g., for FormData)
 * @param {boolean} [options.skipDecryption=false] - Skip response decryption (e.g., for binary)
 * @param {boolean} [options.rawBody=false] - Send body as-is (e.g., FormData)
 * @returns {Promise<Object>} Parsed and decrypted response
 */
export const coreApiRequest = async (endpoint, options = {}) => {
  const url = `${resolveCoreApiBaseUrl()}${endpoint}`;
  const token = getAuthToken();

  // Prepare headers
  const headers = {
    ...(options.headers || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Prepare body
  let requestBody = undefined;
  if (options.body && !options.rawBody) {
    // Encrypt the body and wrap in { data: encrypted }
    headers['Content-Type'] = 'application/json';
    const encrypted = encryptPayload(options.body);
    requestBody = JSON.stringify({ data: encrypted });
  } else if (options.rawBody && options.body) {
    // Send as-is (e.g., FormData - browser sets Content-Type automatically)
    requestBody = options.body;
  }

  // Add timeout to prevent hanging when backend is unresponsive
  const timeoutMs = options.timeout || 30000; // 30s default
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(url, {
      method: options.method || 'GET',
      headers,
      body: requestBody,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  // For non-JSON responses (binary files, etc.)
  if (options.skipDecryption) {
    return { response, ok: response.ok };
  }

  // Parse response
  const responseData = await response.json();

  if (!response.ok) {
    const errorMsg = responseData.error || responseData.message || `Request failed (${response.status})`;
    throw new Error(errorMsg);
  }

  // Decrypt the data field if it's an encrypted string
  if (responseData.data && typeof responseData.data === 'string') {
    try {
      responseData.data = decryptPayload(responseData.data);
    } catch (decryptError) {
      // If decryption fails, the response may not be encrypted (backward compat)
      console.warn('Response data decryption failed, using raw data:', decryptError.message);
    }
  }

  return responseData;
};

/**
 * Convenience methods for common HTTP operations
 */
export const coreApi = {
  get: (endpoint, options = {}) =>
    coreApiRequest(endpoint, { ...options, method: 'GET' }),

  post: (endpoint, body, options = {}) =>
    coreApiRequest(endpoint, { ...options, method: 'POST', body }),

  put: (endpoint, body, options = {}) =>
    coreApiRequest(endpoint, { ...options, method: 'PUT', body }),

  delete: (endpoint, options = {}) =>
    coreApiRequest(endpoint, { ...options, method: 'DELETE' }),

  /**
   * Upload files via FormData (no body encryption, but response is still decrypted)
   */
  upload: (endpoint, formData, options = {}) =>
    coreApiRequest(endpoint, { ...options, method: 'POST', body: formData, rawBody: true }),
};

export default coreApi;
